import type {ComboboxItem} from '@mantine/core';
import {Button} from '@mantine/core';
import {Center} from 'src/design/atoms/Center';
import React from 'react';
import type {
  NotificationRuleMetadata,
  NotificationRuleReminderCondition,
} from 'src/api/ProactApiClient';
import {
  ModuleEnum,
  NotificationRuleReminderConditionPropertyEnum,
  RelationalOperatorEnum,
} from 'src/api/ProactApiClient';
import {NoItemsPanel} from 'src/design/molecules/NoItemsPanel';
import {IconPlus} from '@tabler/icons-react';
import {EditNotificationReminderRule} from './EditNotificationReminderRule';

export interface EditNotificationReminderRulesProps {
  data: NotificationRuleMetadata;
  onDataChange(data: NotificationRuleMetadata): void;
  disabled: boolean;
  classificationOptions?: ComboboxItem[];
  attachmentTypeOptions?: ComboboxItem[];
}
export const EditNotificationReminderRules: React.FC<
  EditNotificationReminderRulesProps
> = ({
  data,
  onDataChange,
  disabled,
  classificationOptions,
  attachmentTypeOptions,
}) => {
  function handleNew(): void {
    onDataChange({
      ...data,
      reminderConditions: [
        ...(data.reminderConditions ?? []),
        {
          property: NotificationRuleReminderConditionPropertyEnum.DueDate,
          operator: RelationalOperatorEnum.GreaterThan,
          value: '5',
        } as NotificationRuleReminderCondition,
      ],
    });
  }

  function handleChange(
    entity: NotificationRuleReminderCondition,
    index: number
  ): void {
    if (data.reminderConditions === undefined) {
      return;
    }

    const newEntities = Array.from(data.reminderConditions);
    newEntities[index] = entity;
    onDataChange({
      ...data,
      reminderConditions: newEntities,
    });
  }

  function handleDelete(
    entity: NotificationRuleReminderCondition,
    index: number
  ): void {
    if (data.reminderConditions === undefined) {
      return;
    }

    onDataChange({
      ...data,
      reminderConditions: [
        ...data.reminderConditions.slice(0, index),
        ...data.reminderConditions.slice(
          index + 1,
          data.reminderConditions.length
        ),
      ],
    });
  }

  return (
    <>
      {data.reminderConditions && data.reminderConditions.length > 0 ? (
        <>
          {data.reminderConditions?.map((condition, index) => {
            return (
              <EditNotificationReminderRule
                key={index}
                module={data.module ?? ModuleEnum.Invalid}
                data={condition}
                index={index}
                disabled={disabled}
                onChange={handleChange}
                onDelete={handleDelete}
                classificationOptions={classificationOptions}
                attachmentTypeOptions={attachmentTypeOptions}
              />
            );
          })}
          <Center>
            <Button
              aria-label="addNewBtnUI"
              onClick={handleNew}
              size="sm"
              leftSection={<IconPlus />}
              disabled={disabled}>
              Add Condition
            </Button>
          </Center>
        </>
      ) : (
        <NoItemsPanel
          canCreate={true}
          createNode={
            <Button
              aria-label="addNewBtnUI"
              onClick={handleNew}
              size="sm"
              leftSection={<IconPlus />}
              disabled={disabled}>
              Add First Condition
            </Button>
          }
          noItemsText="Conditions determine when reminder notifications are sent. Add conditions to define the timing criteria for your reminders."
        />
      )}
    </>
  );
};
